import { useInfiniteQuery } from '@tanstack/react-query';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';

import { Badge } from '@doger/ui/components/ui/badge';
import {
  Breadcrumb,
  type BreadcrumbItem,
} from '@doger/ui/components/ui/breadcrumb';
import { Button } from '@doger/ui/components/ui/button';
import { Skeleton } from '@doger/ui/components/ui/skeleton';

import {
  $$privateApi,
  type FindManyUsersResponse,
} from '@doger/admin/shared/api';

import styles from './users.module.scss';

const USERS_PER_PAGE = 20;

export const UsersPage = memo(() => {
  const breadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [{ label: 'Users' }],
    []
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey: ['users'],
    queryFn: ({ pageParam = 0 }) =>
      $$privateApi.findManyUsers({
        take: USERS_PER_PAGE,
        skip: pageParam * USERS_PER_PAGE,
      }),

    initialPageParam: 0,

    getNextPageParam: (lastPage, allPages) =>
      lastPage.length === USERS_PER_PAGE ? allPages.length : undefined,

    staleTime: 0,
  });

  const { ref, inView } = useInView({
    threshold: 0,
  });

  const loadMore = useCallback(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Load more when the last item comes into view
  useEffect(() => {
    loadMore();
  }, [loadMore]);

  const columns = useMemo<ColumnDef<FindManyUsersResponse[number]>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        cell: ({ row }) => (
          <div className={styles.id}>{row.getValue('id')}</div>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => {
          const user = row.original;
          const name =
            user.firstName && user.lastName
              ? `${user.firstName} ${user.lastName}`
              : (user.firstName ?? user.lastName ?? 'N/A');

          return <div className={styles.name}>{name}</div>;
        },
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => (
          <div className={styles.email}>{row.getValue('email')}</div>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Joined At',
        cell: ({ row }) => (
          <div className={styles.joinedAt}>
            {format(new Date(row.getValue('createdAt')), 'MMM dd, yyyy')}
          </div>
        ),
      },
      {
        accessorKey: 'role',
        header: 'Role',
        cell: ({ row }) => (
          <div className={styles.role}>{row.getValue('role')}</div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const user = row.original;

          return (
            <div className={styles.status}>
              <div className={styles.statusContainer}>
                <Badge
                  variant={user.isActive ? 'default' : 'outline'}
                  className={
                    user.isActive ? styles.activeBadge : styles.inactiveBadge
                  }
                >
                  {user.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'whitelist',
        header: 'Whitelist',
        cell: ({ row }) => {
          const user = row.original;

          return (
            <div className={styles.whitelist}>
              <div className={styles.whitelistContainer}>
                <Badge
                  variant={user.isWhitelisted ? 'default' : 'secondary'}
                  className={
                    user.isWhitelisted
                      ? styles.whitelistedBadge
                      : styles.notWhitelistedBadge
                  }
                >
                  {user.isWhitelisted ? 'Whitelisted' : 'Not Whitelisted'}
                </Badge>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const user = row.original;

          return (
            <div className={styles.actions}>
              <Button variant='outlined' size='sm' palette='ghost' asChild>
                <Link to={`/users/${user.id}`}>View</Link>
              </Button>
            </div>
          );
        },
      },
    ],
    []
  );

  const allUsers = data?.pages.flatMap((page) => page) ?? [];

  const table = useReactTable({
    data: allUsers,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return (
      <div className={styles.root}>
        <div className={styles.content}>
          <Breadcrumb items={breadcrumbItems} />
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Joined At</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Whitelist</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 10 }).map((_, index) => (
                  <tr key={index}>
                    <td>
                      <Skeleton className={styles.skeleton} />
                    </td>
                    <td>
                      <Skeleton className={styles.skeleton} />
                    </td>
                    <td>
                      <Skeleton className={styles.skeleton} />
                    </td>
                    <td>
                      <Skeleton className={styles.skeleton} />
                    </td>
                    <td>
                      <Skeleton className={styles.skeleton} />
                    </td>
                    <td>
                      <Skeleton className={styles.skeleton} />
                    </td>
                    <td>
                      <Skeleton className={styles.skeleton} />
                    </td>
                    <td>
                      <Skeleton className={styles.skeleton} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.root}>
        <div className={styles.content}>
          <Breadcrumb items={breadcrumbItems} />
          <div className={styles.error}>
            Error loading users: {error.message}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <Breadcrumb items={breadcrumbItems} />
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              {/* Intersection observer target for infinite scroll */}
              {hasNextPage && (
                <tr ref={ref}>
                  <td colSpan={columns.length} className={styles.loadingRow}>
                    {isFetchingNextPage ? (
                      <div className={styles.loadingMore}>
                        <Skeleton className={styles.skeleton} />
                        <span>Loading more users...</span>
                      </div>
                    ) : null}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
});
